'use strict';

/* global angular */

const _ = require('lodash');
const diff = require('deep-diff');
const moment = require('moment');

/** @AnnotationStore */
const AnnotationStore = function (
  $q, ResourceHelper, Flux, APP_DISPATCHER_ID, SiteStore,
  MapStore, MapActions, NavActions, OverlaySelectorActions,
  TopControlsActions, AnalyticsService
) {

  // Intialize the store
  const AnnotationStore = Flux.Store.create({
    name: 'AnnotationStore',
    events: {
      Changed: true,
      ActiveSaved: true,
      LayerAdded: true,
      LayerRenamed: true,
      Error: false,
      AnnotationDestroyed: true
    }
  });

  const store = AnnotationStore;

  AnnotationStore.COLORS = [
    { color: '#9E1D6D', name: 'Magenta' },
    { color: '#C33532', name: 'Red' },
    { color: '#EA7C2D', name: 'Orange' },
    { color: '#EFAF1E', name: 'Gold' },
    { color: '#F2D012', name: 'Yellow' },
    { color: '#8CBB35', name: 'Spring green' },
    { color: '#44A648', name: 'Forest green' },
    { color: '#00A48F', name: 'Sea green' },
    { color: '#57AFD1', name: 'Sky blue' },
    { color: '#3889BE', name: 'Steel blue' },
    { color: '#005790', name: 'Dark blue' },
    { color: '#4E3A80', name: 'Purple' },
    { color: '#979797', name: 'Medium gray'},
    { color: '#505050', name: 'Dark gray'}
  ];

  /**
   * Initial empty store state
   * @var {Object} $state
   */
  AnnotationStore.$state = {
    current: null, // The annotation(s) currently displayed on the map
    available: null,
    active: null, // Only one annotation that is being actively created/edited
    error: {}
  };

  /**
   * Intialize the store bindings and initial data.
   * @constructor
   */
  AnnotationStore.init = () => {

    const d = Flux.Dispatcher.get(APP_DISPATCHER_ID);

    // annotations
    d.register(MapActions.NewAnnotationStart, store.handleNewAnnotationStart.bind(store));
    d.register(MapActions.EditAnnotationStart, store.handleEditAnnotationStart.bind(store));
    d.register(MapActions.SaveActiveAnnotation, store.handleSaveActiveAnnotation.bind(store));
    d.register(MapActions.DestroyAnnotation, store.handleDestroyAnnotation.bind(store));
    d.register(MapActions.AnnotationEditEnd, store.handleAnnotationEditEnd.bind(store));
    d.register(MapActions.ChangeAnnotationOverlayOpacity, store.handleOpacityChanged.bind(store));
    d.register(NavActions.ContinueWithNavigation, store.continueWithNavigation.bind(store));
    d.register(OverlaySelectorActions.OverlaySelected, store.handleOverlayAdded.bind(store));
    d.register(TopControlsActions.ContinueWithNavigation, store.continueWithNavigation.bind(store));
  };

  /**
   * Sets the internal state for the Store.
   * @param {state} object An AnnotationStore state object.
   */
  AnnotationStore.setState = (state) => {

    angular.extend(AnnotationStore.$state, state);
    AnnotationStore.fetchLayers().then(() => {

      AnnotationStore.emit(AnnotationStore.Events.Changed, AnnotationStore.$state);
    });
  };

  AnnotationStore.handleNewAnnotationStart = () => {

    const Annotation = ResourceHelper.getResource('Annotation');
    const SiteStore = Flux.Store.get('SiteStore');
    const UserStore = Flux.Store.get('UserStore');
    // TODO: FIXME: Determine if we need to create a new category
    // category if doesn't exist, if it can be optional or we'll have
    // always a default one;
    const newAnnotation = Annotation.createInstance({
      name: null,
      annotationLayers: [],
      siteId: SiteStore.get('current').id,
      userId: UserStore.get('user').id,
      categoryId: 1,
      id: '_tmp_annotation_' + Date.now() + '_' + Math.floor(Math.random() * 10000)
    });
    AnnotationStore.setState({
      active: newAnnotation
    });
  };

  AnnotationStore._activeAnnotationBeforeEdit = null;

  AnnotationStore._revertChanges = () => {

    const previous = AnnotationStore._activeAnnotationBeforeEdit;
    const Annotation = ResourceHelper.getResource('Annotation');

    if (!previous) {
      return;
    }

    Annotation.inject(_removeCircularRelations(previous));
    AnnotationStore._activeAnnotationBeforeEdit = null;
  };

  // Determine if `active` annotation changed from original
  AnnotationStore.activeAnnotationChanged = () => {

    const previous = AnnotationStore._activeAnnotationBeforeEdit;
    const current = AnnotationStore.get('active');

    // New annotation
    if (current && !previous) {

      return true;
    }

    let changed = false;
    diff.observableDiff(previous, current, (change) => {

      // Only observe changes in the layers or the annotation name, which are the
      // only editable properties
      if (change.path && change.path[0] !== 'annotationLayers' && change.path[0] !== 'name') {
        return;
      }

      // Ignore case when annotation reference in layer changes. Object is
      // being serialized from an `Annotation` jsdata model to a plain object
      if (change.path && change.path[0] === 'annotationLayers' &&
          change.path[2] && change.path[2] === 'annotation') {
        return;
      }

      changed = true;
    });
    return changed;
  };

  AnnotationStore.handleEditAnnotationStart = (ev) => {

    const Annotation = ResourceHelper.getResource('Annotation');
    const annotation = Annotation.get(ev.annotationId);
    AnnotationStore._activeAnnotationBeforeEdit = _.cloneDeep(annotation);
    AnnotationStore.setState({
      active: annotation
    });
  };

  AnnotationStore.handleAnnotationEditEnd = (ev) => {

    const save = ev.saveChanges;
    if (save !== undefined && !save) {
      AnnotationStore._revertChanges();
    }
    AnnotationStore.setState({
      active: null
    });
  };

  AnnotationStore.handleOverlayAdded = (ev) => {

    const overlay = ev.data.overlay;
    if (overlay) {
      // Build manually the layer since this doesn't have a toGeoJSON method
      const newLayer = {
        type: 'overlay',
        label: overlay.name,
        data: {
          type: 'Feature',
          properties: {
            style: {},
            overlayId: overlay.id
          },
          geometry: {
            type: 'Point',
            coordinates: [-122.01059281826019, 37.33640155969563] // TODO: Change
          },
          bbox: [
            -122.0108664035797, 37.33622668706485,
            -122.01031923294067, 37.3365764323264
          ] // TODO: Change
        },
        overlayId: overlay.id
      };
      AnnotationStore.addLayer(newLayer);
    }
  };

  // This method changes the layer opacity, but doesn't propagate the changes,
  // since the change is temporary during the user session and the MapController
  // is in charge of syncing the opacity layer in the map booster
  AnnotationStore.handleOpacityChanged = (ev) => {

    const annotation = _.find(AnnotationStore.get('available'), 'id', ev.annotationId);
    const layer = _.find(annotation.annotationLayers, 'id', ev.annotationLayerId);
    if (layer.overlay) {
      layer.overlay.opacity = ev.opacity;
    }
  };

  AnnotationStore.continueWithNavigation = () => {

    AnnotationStore.handleAnnotationEditEnd({ saveChanges: false });
  };

  /*
   * Removes the related attributes that cause a circular dependency between
   * models before injecting
   * Note: jsdata introduces `revert` in a newer build, but it doesn't work
   * with current implementation. User `inject` instead, making sure we strip
   * the related attribute `site` (jsdata throws an error if not removed)
   */
  const _removeCircularRelations = (annotation) => {

    annotation.annotationLayers.forEach((layer) => {

      delete layer.annotation;
      if (layer.overlay) {
        // Prevents a recursive error that ends up with a stack overflow error
        delete layer.overlay.site;
      }
    });


    delete annotation.site;
    return annotation;
  };

  // TODO: REFACTOR:
  // Use a whitelist of attributes instead of removing them individually
  const _formatAnnotation = (annotation) => {

    const formatted = _.cloneDeep(annotation);
    formatted.annotationLayers.forEach((layer) => {

      if (layer.id && /^_tmp/.test(layer.id)) {
        delete layer.id;
      }
      // TODO: Map booster will put some calculated properties in the formatted
      // data for some types of annotations. We agreed that they will go into the
      // `property` attribute of the resulting geoJSON, so we need to strip them
      // from the layer attributes for now. This won't be needed when that
      // change is made on the booster, but it needs to be sorted out first on
      // its usage in Commander (ruben 25-08-15)
      delete layer.area;
      delete layer.circumference;
      delete layer.distance;

      delete layer.annotation; // Remove relationship
      delete layer.popupLatlng; // Remove popup reference set in layer
      delete layer.overlay; // Remove overlay reference
      delete layer.opacity; // This is a temporary value, just for the user session

      // TODO: FIXME - These come from the way the filtered lists on the navbar
      // interact with the elements passed to it (needs to add these internal
      // attributes, and we are not using a copy of the object).
      delete layer.$$hashKey;
      delete layer.$$layerUID;

      delete layer.createdAt;
      delete layer.updatedAt;
      delete layer._permissions;
    });
    delete formatted.site;
    delete formatted.user;
    delete formatted.userName;
    delete formatted.createdAt;
    delete formatted.updatedAt;
    delete formatted._permissions; // Comes with data but is not part of attributes
    delete formatted.$$hashKey; // See above
    delete formatted.$$layerUID; // See above

    if (formatted.id && /^_tmp/.test(formatted.id)) {
      delete formatted.id;
    }

    if (!formatted.name) {
      const formattedDate = moment(new Date()).format('l LTS');
      formatted.name = `Annotation ${formattedDate}`;
    }
    return formatted;
  };

  AnnotationStore.handleSaveActiveAnnotation = () => {

    const Annotation = ResourceHelper.getResource('Annotation');
    const AnnotationLayer = ResourceHelper.getResource('AnnotationLayer');
    const annotation = AnnotationStore.get('active');
    if (!annotation) {
      return;
    }

    const site = SiteStore.get('current');

    if (annotation.id && !/^_tmp/.test(annotation.id)) {
      // Comes from an 'edit' action
      const toSave = _formatAnnotation(annotation);

      // TODO FIXME: This is a limitation on the relations of jsdata, where we can't
      // track the deleted layers passed along with the _withRelated data. If we need
      // this behavior for some other resource we may need to directly extend jsdata
      const toDelete = []; // IDs of the layers that need to be deleted.

      // Have to sync the deleted layers so the backend can delete them
      if (AnnotationStore._activeAnnotationBeforeEdit) {
        AnnotationStore._activeAnnotationBeforeEdit.annotationLayers.forEach((layer) => {

          if (!_.find(toSave.annotationLayers, 'id', layer.id)) {
            // Mark the internal layer for removal
            toSave.annotationLayers.push({ id: layer.id, _delete: true });
            toDelete.push(layer.id);
          }
        });
      }

      Annotation.update(annotation.id, toSave, {
        params: {
          include: 'annotationLayers',
          linkInverse: false
        }
      })
      .then((annotation) => {

        // See note above `@toDelete`
        AnalyticsService.track('[CORE] Annotations', 'Edit Annotation', {
          annotationId: annotation.id,
          siteId: site.id,
          siteName: site.name
        });
        toDelete.forEach((deletedId) => {

          AnnotationLayer.eject(deletedId);
        });
        AnnotationStore.emit(AnnotationStore.Events.ActiveSaved);

        // Clear cache
        AnnotationStore._activeAnnotationBeforeEdit = null;
      })
      .catch((err) => {

        AnalyticsService.track('[CORE] Annotations', 'Edit Annotation Error', {
          annotationId: annotation.id,
          siteId: site.id,
          siteName: site.name
        });
      });
    } else {
      // Comes from a 'new' action
      Annotation.create(
        _formatAnnotation(annotation),
        { params: { include: 'annotationLayers' } }
      )
      .then((annotation) => {

        // This comes from a virtual attribute when we are requesting it, but
        // since we created it (and the `withRelated` attribute has a different
        // meaning on creation), need to add it explicitely from here
        const UserStore = Flux.Store.get('UserStore');
        const currentUser = UserStore.get('user');
        let availableAnnotations, current, active;

        annotation.userName = {
          firstName: currentUser.firstName,
          lastName: currentUser.lastName
        };

        // When the annotation is saved, the id changes so we need to fetch the
        // object from the available list, and add it to the `current` list so
        // the new annotation displays immediately rendered on the map
        availableAnnotations = SiteStore.get('current').annotations;
        current = AnnotationStore.$state.current;
        active = _.find(availableAnnotations, 'id', annotation.id);

        if (active && !_.find(current, 'id', active.id)) {
          current.push(active);
        }

        // We need to set the store with the new values but not trigger the
        // change event, the Events.ActiveSaved triggers a ChangedEvent itself.
        // Fixes issue in Safari not updating the UI after the two events fire.
        AnnotationStore.$state.active = annotation;
        AnnotationStore.$state.available = availableAnnotations;
        AnnotationStore.$state.current = current;

        AnnotationStore.emit(AnnotationStore.Events.ActiveSaved);

        AnalyticsService.track('[CORE] Annotations', 'Create Annotation', {
          annotationId: annotation.id,
          siteId: site.id,
          siteName: site.name
        });
      })
      .catch((err) => {

        AnalyticsService.track('[CORE] Annotations', 'Create Annotation Error', {
          annotationId: annotation.id,
          siteId: site.id,
          siteName: site.name
        });
        AnnotationStore.setState({
          error: err.data.error
        });
        AnnotationStore.emit(AnnotationStore.Events.Error);
      });

    }
  };

  AnnotationStore.renameAnnotationLayer = (layerId, newName) => {

    const active = AnnotationStore.get('active');
    if (!active) {
      return;
    }
    const layer = _.find(active.annotationLayers, 'id', layerId);
    layer.label = newName;
    AnnotationStore.emit(AnnotationStore.Events.LayerRenamed);
  };

  AnnotationStore.handleDestroyAnnotation = (ev) => {

    const Annotation = ResourceHelper.getResource('Annotation');
    const annotation = _.find(AnnotationStore.get('available'), 'id', ev.annotationId);
    const currentSite = SiteStore.get('current');
    if (!annotation) {
      return;
    }
    Annotation.destroy(annotation)
    .then(() => {

      Annotation.eject(annotation);
      AnnotationStore.setState({
        // TODO: Hook so these update automatically, see note above on
        // handleSaveActiveAnnotation on save callback
        available: currentSite.annotations,
        current: _.reject(AnnotationStore.$state.current, 'id', annotation.id)
      });
      AnalyticsService.track('[CORE] Annotations', 'Delete Annotation', {
        annotationId: annotation.id,
        siteId: currentSite.id,
        siteName: currentSite.name
      });
      AnnotationStore.emit(AnnotationStore.Events.AnnotationDestroyed);
    });
  };

  AnnotationStore.addLayer = (layer) => {

    const active = AnnotationStore.get('active');
    if (!active) {
      throw new Error('No active annotation to add layer to');
    }
    layer.id = '_tmp_annotation-layer_' + Date.now() + '_' + Math.floor(Math.random() * 10000);
    layer.annotationId = active.id;
    active.annotationLayers.unshift(layer);
    // FIXME: Revise: Even though the state doesn't really change since the
    // `active` object is the same, the dispatcher signals the Events.Changed
    // event. This behavior is unspecified as it is lacking a proper spec, so
    // be wary if the implementation ever changes, it may introduce a bug in
    // which the new layers do not get added to the map
    AnnotationStore.setState({
      active: active
    });
    AnnotationStore.emit(AnnotationStore.Events.LayerAdded, layer.id);
  };

  /**
   * Adds the annotation with id `id` to the list of `current` Annotations,
   * checking first that it is part of the `available` annotations list
   */
  AnnotationStore.activate = (id) => {

    const available = AnnotationStore.get('available');
    const current = AnnotationStore.get('current');
    if (!current) {
      // Should not enter here, current should be at least an empty array
      return;
    }
    // Do nothing if it's already active
    if (!_.find(current, 'id', id)) {
      const element = _.find(available, 'id', id);
      // Requested an annotation that was not available
      if (!element) {
        console.error('Annotation requested not found: ', id);
      } else {
        AnalyticsService.track('[CORE] Annotations', 'View Annotation', {
          annotationId: element.id,
          siteId: element.siteId,
          siteName: element.site.name
        });
        AnnotationStore.setState({
          current: current.concat([element]) // Force a new array to trigger changes
        });
      }
    }
  };

  /**
   * Removes the annotation with id `id` to the list of `current` Annotations
   * if present
   */
  AnnotationStore.deactivate = (id) => {

    const current = AnnotationStore.get('current');
    if (!current) {
      return;
    }
    const element = _.find(current, 'id', id);
    if (element) {
      AnnotationStore.setState({
        current: _.reject(current, 'id', id) // Force a new array to trigger changes
      });
    }
  };

  AnnotationStore.getFormattedAnnotations = (annotations) => {

    return annotations.map((annotation) => {

      return {
        id: annotation.id,
        name: annotation.name,
        layers: _(annotation.annotationLayers)
          .reject('type', 'overlay')
          .value()
      };
    });
  };

  /*
   * Setter for changing the name of the active annotation. This is to circumvent
   * the cloned object returned by the `Store.get` method and use directly the
   * in memory object
   */
  AnnotationStore.changeActiveName = (name) => {

    const _active = store.$state.active;
    if (_active) {
      _active.name = name;
    }
  };

  AnnotationStore.isNewRecord = (annotation) => {

    const exists = annotation && annotation.id && !/^_tmp/.test(annotation.id);
    return !exists;
  };

  //////////////////////////////////////////////////////////////////////////////
  // Model Helpers
  //////////////////////////////////////////////////////////////////////////////
  // TODO: If this gets used in more places, we should refactor the logic to a
  // central module. We could integrate this as part of all models and fetch the
  // permission into the serialization process done by Resource base (js-data)
  // (i.e. annotation._permissions.read)
  AnnotationStore.getPermissions = (annotationId) => {

    const UserStore = Flux.Store.get('UserStore');
    const userId = UserStore.get('user').id;
    const annotation = _.find(AnnotationStore.get('available'), 'id', annotationId);

    if (!annotation) {
      return {};
    }

    return {
      read: true, // If got here, is because is readable in first place
      edit: annotation.userId === userId
    };
  };

  //////////////////////////////////////////////////////////////////////////////
  // Utility Methods
  //////////////////////////////////////////////////////////////////////////////

  const _DEFAULT_PATH_COLOR = '#3889BE';

  // Gets the name an annotation layer should have, based on the actual data,
  // the type (so determine if should contain color names or text), and an
  // optional newColor param that will be used to assign or replace the
  // existing one. It only replaces the name when it contains a color iff the
  // previous color matcher the `{color} {type}` naming schema
  AnnotationStore.getAnnotationLayerName = (layerData, type, newColor, oldLabel) => {

    let colorCode, colorName, defaultName;
    defaultName = 'New ' + type;

    if (type === 'text' || type === 'marker') {
      if (layerData.properties && layerData.properties.text) {
        return layerData.properties.text || defaultName;
      }
      return oldLabel || defaultName;
    }

    if (newColor) {
      colorCode = newColor;
    }
    else if (layerData.properties && layerData.properties.style) {
      colorCode = layerData.properties.style.color;
    }
    else {
      colorCode = _DEFAULT_PATH_COLOR;
    }

    colorName = _colorToName(colorCode);
    // Only change it when it matches the default naming schema,
    // to avoid overwriting of custom names
    if (!oldLabel || oldLabel.match(new RegExp('(\\w+ )+' + type))) {
      if (colorName) {
        return colorName + ' ' + type;
      }
      else {
        return defaultName;
      }
    }
    else {
      return oldLabel || defaultName;
    }
  };

  // Returns the human name for the color, trying to get the name
  // of "registered" colors. If not found, it will return null
  const _colorToName = (colorCode) => {

    const color = _.find(AnnotationStore.COLORS, 'color', colorCode);
    if (color) {
      return color.name;
    }
    return null;
  };

  AnnotationStore._FETCHED_ANNOTATIONS = [];

  AnnotationStore.fetchLayers = () => {

    const active = AnnotationStore.$state.active;
    const current = AnnotationStore.$state.current;
    const promises = [];
    const Annotation = ResourceHelper.getResource('Annotation');
    if (active && !/^_tmp/.test(active.id)) {
      if (_.isEmpty(active.annotationLayers) && !_.find(AnnotationStore._FETCHED_ANNOTATIONS, active.id)) {
        promises.push(
          Annotation.find(active.id, {
            params: { include: 'annotationLayers' }
          })
          .then(() => {

            AnnotationStore._FETCHED_ANNOTATIONS.push(active.id);
          })
        );
      }
    }

    if (current) {
      current.forEach((annotation) => {

        if (!/^_tmp/.test(annotation.id)) {
          if (_.isEmpty(annotation.annotationLayers) && !_.find(AnnotationStore._FETCHED_ANNOTATIONS, annotation.id)) {
            promises.push(
              Annotation.find(annotation.id, {
                params: { include: 'annotationLayers' }
              })
              .then(() => {

                AnnotationStore._FETCHED_ANNOTATIONS.push(annotation.id);
              })
            );
          }
        }
      });
    }
    return $q.all(promises);
  };

  //////////////////////////////////////////////////////////////////////////////
  // Event Handlers
  //////////////////////////////////////////////////////////////////////////////

  store.handleSiteChange = () => {

    const currentSite = SiteStore.get('current');
    if (currentSite && currentSite.annotations) {
      AnnotationStore.setState({
        available: currentSite.annotations.filter((a) => {

          return !a.isDeleted;
        }),
        current: [],
        active: null
      });
    }
  };

  store.handleDeleteLayer = () => {

    const activeLayer = MapStore.get('activeAnnotationLayerId');
    const activeAnnotation = AnnotationStore.get('active');
    if (activeLayer && activeAnnotation) {
      _.remove(activeAnnotation.annotationLayers, 'id', activeLayer);
      AnnotationStore.setState({
        active: activeAnnotation
      });
      MapStore.$set('activeAnnotationLayerId', null);
    }
  };

  SiteStore.on(SiteStore.Events.Changed, store.handleSiteChange.bind(store));
  MapStore.on(MapStore.Events.DeleteActiveLayer, store.handleDeleteLayer.bind(store));

  return AnnotationStore;
};

AnnotationStore.$inject = [
  '$q', 'ResourceHelper', 'sbFlux', 'APP_DISPATCHER_ID', 'SiteStore',
  'MapStore', 'MapActions', 'NavActions', 'OverlaySelectorActions',
  'TopControlsActions', 'AnalyticsService'
];

module.exports = AnnotationStore;



// WEBPACK FOOTER //
// ./src/app/dash/dataStores/AnnotationStore.js